import { statusLabel } from "../lib/departmentWorkflow";
import type { WorkspaceLanguage } from "../lib/i18n";
import { getPerspectiveDisplayName } from "../lib/valueWorkflow";
import { getWorkshopQuestions } from "../lib/workshopQuestions";
import type { Department, DepartmentResponse } from "../types";

interface DepartmentResponseViewProps {
  department: Department;
  response?: DepartmentResponse;
  audience: "admin" | "owner" | "contributor";
  language?: WorkspaceLanguage;
}

export function DepartmentResponseView({ department, response, audience, language = "de" }: DepartmentResponseViewProps) {
  const questions = getWorkshopQuestions(language);
  const copy = language === "en" ? {
    noResponse: "No answers have been submitted for this department yet.",
    notSubmitted: "Not submitted yet",
    adminNote: "This view is only visible to HAUFFE Admins.",
    ownerNote: "These answers were submitted by the invited department and are included in the shared Customer Value Evaluation.",
    contributorNote: "This view only shows your own department perspective.",
    department: "Department",
    status: "Status",
    submittedAt: "Submitted at",
    noAnswer: "No answer entered."
  } : {
    noResponse: "Für diese Abteilung liegen noch keine Antworten vor.",
    notSubmitted: "Noch nicht eingereicht",
    adminNote: "Diese Ansicht ist nur für HAUFFE Admin sichtbar.",
    ownerNote: "Diese Antworten wurden von der eingeladenen Abteilung eingereicht und fließen in die gemeinsame Customer-Value-Auswertung ein.",
    contributorNote: "Diese Ansicht zeigt ausschließlich Ihre eigene Abteilungsperspektive.",
    department: "Abteilung",
    status: "Status",
    submittedAt: "Eingereicht am",
    noAnswer: "Keine Antwort eingetragen."
  };

  if (!response) {
    return <p>{copy.noResponse}</p>;
  }

  const submittedAt = response.submittedAt ? new Date(response.submittedAt).toLocaleString(language === "en" ? "en-US" : "de-DE") : copy.notSubmitted;
  const visibilityNote =
    audience === "admin"
      ? copy.adminNote
      : audience === "owner"
        ? copy.ownerNote
        : copy.contributorNote;

  return (
    <div className="response-view">
      <div className="data-list">
        <div>
          <span>{copy.department}</span>
          <strong>{getPerspectiveDisplayName(department, language)}</strong>
        </div>
        <div>
          <span>{copy.status}</span>
          <strong className={`status-badge status-${response.responseStatus}`}>{statusLabel(response.responseStatus, language)}</strong>
        </div>
        <div>
          <span>{copy.submittedAt}</span>
          <strong>{submittedAt}</strong>
        </div>
      </div>
      <p className="muted">{visibilityNote}</p>
      <div className="question-flow">
        {questions.map((question, index) => (
          <article key={question.id} className="question-card">
            <span className="question-number">{String(index + 1).padStart(2, "0")}</span>
            <strong>{question.label}</strong>
            <p>{response.answers[question.id] || copy.noAnswer}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
